<div class="search-container" *ngIf="hasAccess(['ADMIN', 'USER'])">
  <div class="search-input-wrapper" [class.focused]="isOpen">
    <mat-icon class="search-icon">search</mat-icon>

    <input
      #searchInput
      type="text"
      class="search-input"
      placeholder="Search leads or activity"
      [formControl]="searchControl"
      [matAutocomplete]="auto"
      (focus)="handleFocus()"
    />

    <div class="search-actions">
      <mat-spinner *ngIf="isLoading" diameter="20" class="search-spinner"></mat-spinner>
      <button
        *ngIf="searchControl.value && !isLoading"
        mat-icon-button
        class="clear-button"
        (click)="closeSearch()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <mat-autocomplete
    #auto="matAutocomplete"
    class="search-autocomplete"
    [displayWith]="displayFn"
    (optionSelected)="onResultClick($event.option.value)"
    [autoActiveFirstOption]="false"
  >
    <!-- Force panel to stay open with invisible option when needed -->
    <mat-option *ngIf="false" [value]="null" style="display: none;"></mat-option>

    <!-- Recent Searches Section - Only show when no search term -->
    <div
      *ngIf="!searchControl.value && !showNoResultsMessage && recentSearches.length > 0"
      class="search-section"
    >
      <div class="section-header">
        <mat-icon>history</mat-icon>
        <span>Recent Searches</span>
        <button
          mat-icon-button
          class="clear-history-button"
          (click)="clearRecentSearches()"
          matTooltip="Clear history"
        >
          <mat-icon>clear_all</mat-icon>
        </button>
      </div>

      <mat-option
        *ngFor="let recent of recentSearches"
        [value]="recent"
        class="search-result-item"
      >
        <div class="result-content">
          <div class="result-left">
            <mat-icon class="result-icon" [style.color]="getTypeColor(recent.type)">
              {{ getResultIcon(recent.type) }}
            </mat-icon>
            <div class="result-info">
              <div class="result-title">{{ recent.title }}</div>
              <div class="result-subtitle">{{ recent.subtitle }}</div>

              <div class="result-details" *ngIf="recent.type === 'lead' && recent.details">
                <span *ngIf="recent.details.program" class="detail-item">
                  <mat-icon>school</mat-icon> {{ recent.details.program }}
                </span>
                <span *ngIf="recent.details.campus" class="detail-item">
                  <mat-icon>location_on</mat-icon> {{ recent.details.campus }}
                </span>
              </div>

              <div class="result-details" *ngIf="recent.type === 'activity' && recent.details">
                <span *ngIf="recent.details.dueDate" class="detail-item">
                  <mat-icon>schedule</mat-icon> {{ formatDate(recent.details.dueDate) }}
                </span>
                <span *ngIf="recent.details.type" class="detail-item">
                  <mat-icon>label</mat-icon> {{ recent.details.type }}
                </span>
              </div>
            </div>
          </div>

          <div class="result-right">
            <mat-chip
              *ngIf="recent.details.status"
              [class]="'status-chip status-' + recent.details.status.toLowerCase()"
            >
              {{ recent.details.status }}
            </mat-chip>
            <mat-chip
              *ngIf="recent.type === 'lead' && recent.details.step"
              class="step-chip"
            >
              {{ recent.details.step }}
            </mat-chip>
          </div>
        </div>
      </mat-option>
    </div>

    <!-- Quick Search Suggestions - Only show when no search term and no recent searches -->
    <div
      *ngIf="!searchControl.value && !showNoResultsMessage && recentSearches.length === 0"
      class="search-section"
    >
      <div class="section-header">
        <mat-icon>lightbulb</mat-icon>
        <span>Try searching for</span>
      </div>
      <div class="quick-search-chips">
        <mat-chip
          *ngFor="let term of popularSearchTerms"
          (click)="onQuickSearch(term)"
          class="quick-search-chip"
        >
          {{ term }}
        </mat-chip>
      </div>
    </div>

    <!-- No Results Message for short search terms -->
    <mat-option
      *ngIf="showNoResultsMessage && searchControl.value && searchControl.value.length <= 2"
      disabled
      class="no-results-option"
    >
      <div class="no-results">
        <mat-icon>info_outline</mat-icon>
        <p>Type more than 2 characters to search</p>
        <small>Keep typing to search for leads and activities</small>
      </div>
    </mat-option>

    <!-- No Results Message for empty results -->
    <mat-option
      *ngIf="noResults && !isLoading && searchControl.value && searchControl.value.length > 2"
      disabled
      class="no-results-option"
    >
      <div class="no-results">
        <mat-icon>search_off</mat-icon>
        <p>No results found for "{{ searchControl.value }}"</p>
        <small>Try searching with different keywords</small>
      </div>
    </mat-option>

    <!-- Search Results -->
    <ng-container
      *ngIf="
        searchControl.value &&
        searchControl.value.length > 2 &&
        !showNoResultsMessage &&
        (searchResults$ | async) as results
      "
    >
      <ng-container *ngIf="!noResults && results.length > 0">
        <!-- Leads Section -->
        <ng-container *ngIf="getLeadResults(results).length > 0">
          <div class="section-header">
            <mat-icon>person</mat-icon>
            <span>Leads ({{ getLeadResults(results).length }})</span>
          </div>

          <mat-option
            *ngFor="let result of getLeadResults(results); let i = index"
            [value]="result"
            class="search-result-item"
            [class.selected]="selectedIndex === i"
          >
            <div class="result-content">
              <div class="result-left">
                <mat-icon class="result-icon" style="color: #0a7ea4">person</mat-icon>
                <div class="result-info">
                  <div class="result-title">{{ result.title }}</div>
                  <div class="result-subtitle">{{ result.subtitle }}</div>
                  <div class="result-details" *ngIf="result.details">
                    <span *ngIf="result.details.program" class="detail-item">
                      <mat-icon>school</mat-icon> {{ result.details.program }}
                    </span>
                    <span *ngIf="result.details.campus" class="detail-item">
                      <mat-icon>location_on</mat-icon> {{ result.details.campus }}
                    </span>
                    <span *ngIf="result.details.owner" class="detail-item">
                      <mat-icon>person_outline</mat-icon> {{ result.details.owner }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="result-right">
                <mat-chip
                  *ngIf="result.details.status"
                  [class]="'status-chip status-' + result.details.status.toLowerCase()"
                >
                  {{ result.details.status }}
                </mat-chip>
                <mat-chip *ngIf="result.details.step" class="step-chip">
                  {{ result.details.step }}
                </mat-chip>
              </div>
            </div>
          </mat-option>
        </ng-container>

        <!-- Activities Section -->
        <ng-container *ngIf="getActivityResults(results).length > 0">
          <mat-divider *ngIf="getLeadResults(results).length > 0"></mat-divider>
          <div class="section-header">
            <mat-icon>event_note</mat-icon>
            <span>Activities ({{ getActivityResults(results).length }})</span>
          </div>

          <mat-option
            *ngFor="let result of getActivityResults(results); let i = index"
            [value]="result"
            class="search-result-item"
            [class.selected]="isActivitySelected(results, i)"
          >
            <div class="result-content">
              <div class="result-left">
                <mat-icon class="result-icon" style="color: #f59e0b">event_note</mat-icon>
                <div class="result-info">
                  <div class="result-title">{{ result.title }}</div>
                  <div class="result-subtitle">{{ result.subtitle }}</div>
                  <div class="result-details">
                    <span
                      *ngIf="result.details.dueDate"
                      class="detail-item"
                      [class.overdue-text]="result.details.status === 'overdue'"
                    >
                      <mat-icon>schedule</mat-icon> {{ formatDate(result.details.dueDate) }}
                    </span>
                    <span *ngIf="result.details.assignedTo" class="detail-item">
                      <mat-icon>person_outline</mat-icon> {{ result.details.assignedTo }}
                    </span>
                    <span *ngIf="result.details.type" class="detail-item">
                      <mat-icon>label</mat-icon> {{ result.details.type }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="result-right">
                <mat-chip
                  *ngIf="result.details.status"
                  [class]="'status-chip status-' + result.details.status.toLowerCase()"
                >
                  {{ result.details.status }}
                </mat-chip>
              </div>
            </div>
          </mat-option>
        </ng-container>
      </ng-container>
    </ng-container>
  </mat-autocomplete>
</div>
